import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'

import { useAccessToken } from 'store'
import { isProfilePathname, useLocalStorageClear } from 'utils'

const apiUrl = process.env.REACT_APP_API_URL
const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

const axiosInstance: AxiosInstance = axios.create({
  baseURL: `${apiUrl}`,
  // DO NOT force withCredentials here; set it per-request if needed
  headers: {
    Accept: 'application/json',
    'X-User-Timezone': userTimezone,
  },
})

// Attach Authorization; do NOT touch withCredentials here
const onRequest = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const token = useAccessToken()
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
}

const convertToUserTimezone = (dateString: string) => {
  if (!dateString || typeof dateString !== 'string') return dateString
  const date = new Date(dateString.replace(' ', 'T') + 'Z')
  return new Intl.DateTimeFormat('en-US', {
    timeZone: userTimezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(date)
}

const onResponse = (response: AxiosResponse): AxiosResponse => {
  if (response.data) {
    const traverse = (obj: any) => {
      for (const key in obj) {
        const val = obj[key]
        if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(val)) {
          try { obj[key] = convertToUserTimezone(val) } catch {}
        } else if (val && typeof val === 'object') {
          traverse(val)
        }
      }
    }
    traverse(response.data)
  }
  return response
}

// keep async, don’t annotate Promise<never>
const onRequestError = async (error: any) => {
  return await Promise.reject(error)
}

const killSession = () => {
  useLocalStorageClear()
  window.location.href = '/'
}

// use unknown/any, guard, and keep async
const onResponseError = async (err: any) => {
  // treat cancels as benign
  if (err?.code === 'ERR_CANCELED' || axios.isCancel?.(err)) {
    return await Promise.reject(err)
  }

  const error = err as AxiosError
  if (error.response?.status === 401) {
    const currentPathname = window.location.pathname
    if (!isProfilePathname(currentPathname)) {
      killSession()
    }
  }
  return await Promise.reject(err)
}

axiosInstance.interceptors.request.use(onRequest, onRequestError)
axiosInstance.interceptors.response.use(onResponse, onResponseError)

export default axiosInstance
